import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { getBearerTokenForApi } from '../../utils/poolSiteAuth';

type Props = {
  /** /api/events/{id}/risk-snapshot */
  src?: string | null;
  alt?: string;
  width?: number;
  height?: number;
  showDownload?: boolean;
  /** 상세 보기용 큰 이미지 */
  large?: boolean;
  onClick?: () => void;
};

const RETRY_DELAYS_MS = [400, 900, 1600, 2800, 4500];

async function fetchSnapshotBlob(url: string): Promise<Blob | null> {
  try {
    const token = getBearerTokenForApi();
    const headers: HeadersInit = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, { headers, credentials: 'include', cache: 'no-store' });
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    // 백엔드 미기동·CORS·네트워크 끊김 등 → 썸네일만 생략
    return null;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 인증이 필요한 이벤트 캡처 썸네일/다운로드.
 * 캡처 파일이 증거 수집보다 늦게 붙는 경우 404가 날 수 있어 짧게 재시도한다.
 */
const EventSnapshotThumb: React.FC<Props> = ({
  src,
  alt = '이벤트 캡처',
  width = 56,
  height = 40,
  showDownload = false,
  large = false,
  onClick,
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    if (!src) {
      setObjectUrl(null);
      setFailed(false);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setFailed(false);
    setObjectUrl(null);

    // cache-bust query (?t=) 는 서버 파일 갱신 후 강제 재조회용
    const fetchUrl = src;

    void (async () => {
      let blob: Blob | null = null;
      try {
        for (let i = 0; i <= RETRY_DELAYS_MS.length; i += 1) {
          if (cancelled) return;
          blob = await fetchSnapshotBlob(fetchUrl);
          if (blob) break;
          if (i >= RETRY_DELAYS_MS.length) break;
          await sleep(RETRY_DELAYS_MS[i]);
        }
      } catch {
        blob = null;
      }
      if (cancelled) return;
      if (!blob) {
        setFailed(true);
        setObjectUrl(null);
        setLoading(false);
        return;
      }
      const u = URL.createObjectURL(blob);
      revoked = u;
      setObjectUrl(u);
      setFailed(false);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [src]);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!src) return;
    try {
      const blob = await fetchSnapshotBlob(src);
      if (!blob) return;
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `event-snapshot-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    } catch {
      /* ignore */
    }
  };

  if (!src || failed) {
    return null;
  }

  if (loading && !objectUrl) {
    return (
      <Box
        sx={{
          width: large ? '100%' : width,
          height: large ? 200 : height,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: large ? 1 : 0.75,
        }}
      >
        <CircularProgress size={large ? 28 : 14} />
      </Box>
    );
  }

  if (!objectUrl) return null;

  if (large) {
    return (
      <Box sx={{ position: 'relative', display: 'block', width: '100%' }}>
        <Box
          component="img"
          src={objectUrl}
          alt={alt}
          onClick={onClick}
          sx={{
            width: '100%',
            height: 'auto',
            maxHeight: 420,
            objectFit: 'contain',
            borderRadius: 1,
            bgcolor: '#111',
            cursor: onClick ? 'pointer' : 'default',
            display: 'block',
          }}
        />
        {showDownload ? (
          <Tooltip title="캡처 다운로드">
            <IconButton
              size="small"
              onClick={handleDownload}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    );
  }

  // 리스트 행: 고정 박스로 행 높이 유지 (letterbox로 행이 커지지 않게)
  return (
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        width,
        height,
        borderRadius: 0.75,
        overflow: 'hidden',
        bgcolor: '#111',
        alignSelf: 'center',
      }}
    >
      <Box
        component="img"
        src={objectUrl}
        alt={alt}
        onClick={onClick}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          cursor: onClick ? 'pointer' : 'default',
          display: 'block',
        }}
      />
      {showDownload ? (
        <Tooltip title="캡처 다운로드">
          <IconButton size="small" onClick={handleDownload} sx={{ position: 'absolute', top: 0, right: 0 }}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export default EventSnapshotThumb;
